// ---------------------------------------------------------------------------
// REAL SUPABASE CLIENT
//
// This build talks to a real Supabase project (Auth + Postgres). Report data
// comes from real RPC functions backed by synthetic seed data; auth is real
// email/password with email confirmation and password reset via SendGrid SMTP.
//
// Config is read from Vite env (see .env.example):
//   VITE_SUPABASE_URL       e.g. https://mgdyiikmyadwkivhpmuh.supabase.co
//   VITE_SUPABASE_ANON_KEY  the anon / publishable key (safe for the browser)
// ---------------------------------------------------------------------------
import { createClient } from '@supabase/supabase-js';

// This is a single-project public demo, so we commit the project URL + the
// *publishable* key as fallback defaults. Publishable/anon keys are designed to
// ship in the browser bundle (access is governed by RLS + RPC grants), so this
// is safe — and it means the Cloudflare build works even if the VITE_* build
// variables aren't set. Set the env vars (local `.env` / Cloudflare build vars)
// to point a build at a different project.
const DEFAULT_SUPABASE_URL = 'https://mgdyiikmyadwkivhpmuh.supabase.co';
const DEFAULT_SUPABASE_ANON_KEY = 'sb_publishable_fQEh0LSysSIxPkS4DC7dMA_fvMxq2Iw';

const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined) || DEFAULT_SUPABASE_URL;
const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) || DEFAULT_SUPABASE_ANON_KEY;

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // Signup-confirm / magic-link / email-change use Supabase's ConfirmationURL,
    // which redirects back with the session in the URL — let the client pick it
    // up. Invite and password-reset instead use token_hash links handled
    // explicitly by AcceptInviteScreen / UpdatePasswordScreen via verifyOtp.
    detectSessionInUrl: true,
    // Implicit flow keeps email links working across devices (e.g. open on
    // phone) — no PKCE code-verifier round-trip required. Fine for this app.
    flowType: 'implicit',
  },
});

// ---------------------------------------------------------------------------
// Region model + profile types
// ---------------------------------------------------------------------------
// A user can be scoped to:
//   * `null`             — full visibility (default for admins / all-regions)
//   * `'WS'` / `'HC'`    — parent buckets: own-fleet or partner network
//   * an exact region name such as `'WS South Australia'` — a single region.
export type TeamRegionAllocation = string | null;

export type Profile = {
  id: string;
  email: string;
  full_name: string | null;
  role: string | null;
  is_admin: boolean;
  team_region_allocation: TeamRegionAllocation;
};

export type TeamRegionOption = {
  value: string;
  label: string;
  blurb: string;
  group: 'WS' | 'HC';
  isParent: boolean;
};

export const TEAM_REGION_OPTIONS: TeamRegionOption[] = [
  { value: 'WS',                   label: 'Own fleet',                    blurb: 'Parent — all in-house regions', group: 'WS', isParent: true  },
  { value: 'WS New South Wales',   label: 'Fleet — New South Wales',      blurb: 'In-house NSW only',             group: 'WS', isParent: false },
  { value: 'WS Queensland',        label: 'Fleet — Queensland',           blurb: 'In-house QLD only',             group: 'WS', isParent: false },
  { value: 'WS South Australia',   label: 'Fleet — South Australia',      blurb: 'In-house SA only',              group: 'WS', isParent: false },
  { value: 'WS Victoria',          label: 'Fleet — Victoria',             blurb: 'In-house VIC only',             group: 'WS', isParent: false },
  { value: 'WS Western Australia', label: 'Fleet — Western Australia',    blurb: 'In-house WA only',              group: 'WS', isParent: false },
  { value: 'HC',                   label: 'Partners',                     blurb: 'Parent — all partner regions',  group: 'HC', isParent: true  },
  { value: 'HC New South Wales',   label: 'Partners — New South Wales',   blurb: 'Partner NSW only',              group: 'HC', isParent: false },
  { value: 'HC Queensland',        label: 'Partners — Queensland',        blurb: 'Partner QLD only',              group: 'HC', isParent: false },
  { value: 'HC South Australia',   label: 'Partners — South Australia',   blurb: 'Partner SA only',               group: 'HC', isParent: false },
  { value: 'HC Victoria',          label: 'Partners — Victoria',          blurb: 'Partner VIC only',              group: 'HC', isParent: false },
  { value: 'HC Western Australia', label: 'Partners — Western Australia',  blurb: 'Partner WA only',              group: 'HC', isParent: false },
];

const TEAM_REGION_VALUES = new Set(TEAM_REGION_OPTIONS.map(o => o.value));

export function isTeamRegionAllocation(value: unknown): value is TeamRegionAllocation {
  return value === null || (typeof value === 'string' && TEAM_REGION_VALUES.has(value));
}

export function teamRegionLabel(value: TeamRegionAllocation): string {
  if (value === null) return 'All regions';
  const opt = TEAM_REGION_OPTIONS.find(o => o.value === value);
  return opt ? opt.label : value;
}
